import { Component, h, Prop, Element, Method } from '@stencil/core';

/**
 * Details Component
 * @slot - Content to be displayed
 */
@Component({
  tag: 'adesso-details',
  styleUrl: 'ad-details.css',
  shadow: true,
})

export class AdessoDetails {

    @Element() el:HTMLElement;
    /** Open State */
    @Prop({reflect: true, mutable: true }) opened: boolean;
    /** The summary title of the hidden area */
    @Prop({reflect: true, mutable: true }) summarytitle: string;
    isClosing: boolean = false;
    isExpanding: boolean = false;
    isAccordion: boolean = false;
    animation: Animation = null;
    details: HTMLDetailsElement;
    summary: HTMLElement;
    content: HTMLElement;

    componentWillLoad() {
        this.isAccordion = this.el.hasAttribute("accordion");
    }

    onClickHandler(e:MouseEvent) {
        if(this.el.animate) { // Check if Browser Supports Web Animation API

            // Stop default behaviour from the browser
            e.preventDefault();
            // Add an overflow on the <details> to avoid content overflowing
            this.details.style.overflow = 'hidden';
            // Check if the element is being closed or is already closed
            if (this.isClosing || !this.details.open) {
                this.open();
            // Check if the element is being openned or is already open
            } else if (this.isExpanding || this.details.open) {
                this.shrink();
            }
        }
    }

    shrink() {
        // Set the element as "being closed"
        this.isClosing = true;

        // Store the current height of the element
        const startHeight = `${this.details.offsetHeight}px`;
        // Calculate the height of the summary
        const endHeight = `${this.summary.offsetHeight}px`;

        // If there is already an animation running
        if (this.animation) {
        // Cancel the current animation
            this.animation.cancel();
        }

        // Start a WAAPI animation
        this.animation = this.details.animate({
            // Set the keyframes from the startHeight to endHeight
            height: [startHeight, endHeight]
        }, {
            duration: 200,
            easing: 'ease-out'
        });

        // When the animation is complete, call onAnimationFinish()
        this.animation.onfinish = () => this.onAnimationFinish(false);
        // If the animation is cancelled, isClosing variable is set to false
        this.animation.oncancel = () => this.isClosing = false;
        
        if(this.isAccordion) {
            this.el.shadowRoot.querySelector('ad-icon').setAttribute('icon', 'plus');
        }
    }

    onAnimationFinish(open:boolean) {
        // Set the open attribute based on the parameter
        this.details.open = open;
        // Clear the stored animation
        this.animation = null;
        // Reset isClosing & isExpanding
        this.isClosing = false;
        this.isExpanding = false;
        // Remove the overflow hidden and the fixed height
        this.details.style.height = this.details.style.overflow = '';
    }

    /** Open the Details Element */
    @Method() 
    async open() {
        // Apply a fixed height on the element
        this.details.style.height = `${this.details.offsetHeight}px`;
        // Force the [open] attribute on the details element
        this.details.open = true;
        // Wait for the next frame to call the expand function
        window.requestAnimationFrame(() => this.expand());
        if(this.isAccordion) {
            this.el.shadowRoot.querySelector('ad-icon').setAttribute('icon', 'minus');
        }
    }

    expand() {
        // Set the element as "being expanding"
        this.isExpanding = true;
        // Get the current fixed height of the element
        const startHeight = `${this.details.offsetHeight}px`;
        // Calculate the open height of the element (summary height + content height)
        const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;
  
        // If there is already an animation running
        if (this.animation) {
          // Cancel the current animation
          this.animation.cancel();
        }
  
        // Start a WAAPI animation
        this.animation = this.details.animate({
          // Set the keyframes from the startHeight to endHeight
          height: [startHeight, endHeight]
        }, {
          duration: 200,
          easing: 'ease-out'
        });
  

        // When the animation is complete, call onAnimationFinish()
        this.animation.onfinish = () => this.onAnimationFinish(true);
        // If the animation is cancelled, isExpanding variable is set to false
        this.animation.oncancel = () => this.isExpanding = false;
      }

    render() {        

        const icon = this.isAccordion ? <ad-icon icon="plus" /> : <ad-icon icon="chevron-right" />;

        return [
            <details ref={detailsEl => this.details = detailsEl as HTMLDetailsElement}>
                <summary onClick={this.onClickHandler.bind(this)} ref={summaryEl => this.summary = summaryEl}>
                    {icon} {this.summarytitle}
                </summary>
                <div class="content themed" ref={contentEl => this.content = contentEl}>
                    <slot name="content"></slot>
                </div>
            </details>
        ]
      }

}